import React, { useEffect, useState } from 'react';
import { api } from '../../api.js';
import { useFeature } from '../../features.js';

// Shared between the Trips list page and the Create Trip page — both let
// dispatch pick Company driver/Owner operator/Outsource for a trip, and
// need to look and behave identically (see tripAssignment-row/-fields/
// -type-toggle CSS, defined once in Trips.css and reused by both).

export const TRIP_TYPE_OPTIONS = [
  { key: 'company',        label: 'Company driver' },
  { key: 'owner_operator', label: 'Owner operator' },
  { key: 'outsource',      label: 'Outsource' },
];

// allowOutsource=false (no 'outsourcing' feature) hides "Outsource" — unless this trip
// is already outsourced, so existing data still shows what it is.
export function TripTypeToggle({ value, onChange, allowOutsource = true }) {
  const options = TRIP_TYPE_OPTIONS.filter(o => o.key !== 'outsource' || allowOutsource || value === 'outsource');
  return (
    <div className="trip-type-toggle">
      {options.map(o => (
        <button key={o.key} type="button"
          className={'trip-type-toggle-btn' + (value === o.key ? ' active' : '')}
          onClick={() => onChange(o.key)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Once an Owner Operator is picked, shows their truck/trailer read-only
// instead of asking for separate Truck/Trailer pickers — their equipment
// lives on their linked Fleet record (owner_operators.fleet_id,
// migration_047), not picked per trip.
export function OwnerOperatorEquipment({ ownerOperatorId, ownerOperators }) {
  const [equipment, setEquipment] = useState(null);
  const oo = ownerOperators.find(o => o.id == ownerOperatorId);
  const fleetId = oo?.fleet_id;

  useEffect(() => {
    if (!fleetId) { setEquipment(null); return; }
    Promise.all([
      api(`/dispatch/fleets/${fleetId}/trucks`).catch(() => ({ assigned: [] })),
      api(`/dispatch/fleets/${fleetId}/trailers`).catch(() => ({ assigned: [] })),
    ]).then(([t, tr]) => setEquipment({ trucks: t.assigned || [], trailers: tr.assigned || [] }));
  }, [fleetId]);

  if (!ownerOperatorId) return null;
  if (!fleetId) return <div className="trip-detail-oo-equipment">No fleet/equipment on file for this owner operator.</div>;
  if (!equipment) return null;

  return (
    <div className="trip-detail-oo-equipment">
      <div>Truck: {equipment.trucks.map(t => t.unit_number).join(', ') || '—'}</div>
      <div>Trailer: {equipment.trailers.map(t => t.unit_number).join(', ') || '—'}</div>
    </div>
  );
}

// Fetches the 5 pickers' option lists once — used by any page that needs
// the assignment editor below.
export function useTripAssignmentLookups() {
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [ownerOperators, setOwnerOperators] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const outsourcing = useFeature('outsourcing');
  useEffect(() => {
    api('/dispatch/drivers').then(setDrivers).catch(() => {});
    api('/dispatch/trucks').then(setTrucks).catch(() => {});
    api('/dispatch/trailers').then(setTrailers).catch(() => {});
    api('/dispatch/owner-operators').then(setOwnerOperators).catch(() => {});
  }, []);
  // Carriers belong to the 'outsourcing' feature (the endpoint refuses others).
  useEffect(() => {
    if (outsourcing) api('/dispatch/carriers').then(setCarriers).catch(() => {});
  }, [outsourcing]);
  return { drivers, trucks, trailers, ownerOperators, carriers };
}

// Toggle + whichever fields the current type needs. `onChange` receives a
// partial patch to merge into the caller's current assignment and persist
// (each field commits immediately — there's no separate Save).
export function TripAssignmentEditor({ assignmentType, driverId, truckId, trailerId, ownerOperatorId, carrierId, lookups, onChange }) {
  const { drivers, trucks, trailers, ownerOperators, carriers } = lookups;
  const type = assignmentType || 'company';
  const outsourcing = useFeature('outsourcing');
  return (
    <>
      <TripTypeToggle value={type} allowOutsource={outsourcing} onChange={val => onChange({
        assignmentType: val,
        driverId: val === 'company' ? driverId : null,
        truckId: val === 'company' ? truckId : null,
        trailerId: val === 'company' ? trailerId : null,
        ownerOperatorId: val === 'owner_operator' ? ownerOperatorId : null,
        carrierId: val === 'outsource' ? carrierId : null,
      })} />
      <div className="trip-assignment-fields">
        {type === 'company' && (
          <>
            <div className="field flex1">
              <label>Driver</label>
              <select value={driverId || ''} onChange={e => onChange({ driverId: e.target.value || null })}>
                <option value="">Select</option>
                {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="field flex1">
              <label>Truck</label>
              <select value={truckId || ''} onChange={e => onChange({ truckId: e.target.value || null })}>
                <option value="">Select</option>
                {trucks.map(t => <option key={t.id} value={t.id}>{t.unit_number}</option>)}
              </select>
            </div>
            <div className="field flex1">
              <label>Trailer</label>
              <select value={trailerId || ''} onChange={e => onChange({ trailerId: e.target.value || null })}>
                <option value="">Select</option>
                {trailers.map(t => <option key={t.id} value={t.id}>{t.unit_number}</option>)}
              </select>
            </div>
          </>
        )}
        {type === 'owner_operator' && (
          <div className="field flex1">
            <label>Owner Operator</label>
            <select value={ownerOperatorId || ''} onChange={e => onChange({ ownerOperatorId: e.target.value || null })}>
              <option value="">Select</option>
              {ownerOperators.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <OwnerOperatorEquipment ownerOperatorId={ownerOperatorId} ownerOperators={ownerOperators} />
          </div>
        )}
        {type === 'outsource' && (
          <div className="field flex1">
            <label>Carrier</label>
            <select value={carrierId || ''} onChange={e => onChange({ carrierId: e.target.value || null })}>
              <option value="">Select</option>
              {carriers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
      </div>
    </>
  );
}

// Read-only line for an already-assigned trip — expects driver_name/
// truck_number/trailer_number/owner_operator_name/carrier_name already
// resolved onto the object (GET /dispatch/trips joins these; the Create
// Trip page resolves them itself from the lookup lists).
export function tripAssignmentSummaryLine(a) {
  const type = a.assignment_type || 'company';
  if (type === 'owner_operator') return `Owner Operator: ${a.owner_operator_name || '—'}`;
  if (type === 'outsource') return `Carrier: ${a.carrier_name || '—'}`;
  return `Driver: ${a.driver_name || '—'} / Truck: ${a.truck_number || '—'} / Trailer: ${a.trailer_number || '—'}`;
}
